import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, q, explain } from '../lib/supabase';
import { useAsync } from '../lib/useAsync';
import { useGroup } from './GroupLayout';
import { resolveMotm } from '../lib/motm';
import { balanceAccuracy } from '../lib/ratings';
import { recomputeGroupRatings } from '../lib/recompute';
import { matchRating, ratingTone } from '../lib/matchRating';
import { aggregateRatings, applyPeerRatings } from '../lib/peerRatings';
import { RATING_STEPS, ratingWord } from '../lib/constants';
import { votingState, closedOnText, VOTING_WINDOW_HOURS } from '../lib/votingWindow';
import Pitch from '../components/Pitch';
import { EmptyState, ErrorBanner, Spinner } from '../components/Shared';
import Icon from '../components/Icon';

function VoteRow({ game, motm, myMemberId, myVote, onVote, voting, window: win }) {
  const closed = !win.open;
  return (
    <div className="vote">
      <h3 className="vote__title">
        Man of the match
        {closed ? (
          <span className="vote__tag vote__tag--shut">
            <Icon name="lock" size={11} />
            settled
          </span>
        ) : (
          <span className="vote__tag">{win.label}</span>
        )}
        {motm.source === 'admin' && <span className="vote__tag">admin's pick — no votes yet</span>}
        {motm.source === 'tie' && <span className="vote__tag">tied — nobody has broken it</span>}
      </h3>
      <div className={`vote__grid ${closed ? 'vote__grid--shut' : ''}`}>
        {game.participants.map((p) => {
          const count = motm.counts[p.memberId] || 0;
          const isWinner = motm.memberId === p.memberId;
          const isMine = myVote === p.memberId;
          // Once it's settled the chips stop being buttons and become the
          // result: the winner and the tally, with your own vote still marked.
          if (closed && !isWinner && count === 0) return null;
          return (
            <button
              type="button"
              key={p.id}
              className={`vote__chip ${isWinner ? 'vote__chip--won' : ''} ${isMine ? 'vote__chip--mine' : ''}`}
              onClick={() => onVote(isMine ? null : p.memberId)}
              disabled={!myMemberId || voting || closed}
              title={
                closed
                  ? 'Voting closed'
                  : myMemberId
                  ? isMine
                    ? 'Tap to withdraw your vote'
                    : `Vote for ${p.name}`
                  : 'Guests cannot vote'
              }
            >
              {isWinner && <Icon name="star" size={12} />}
              {p.name}
              {count > 0 && <span className="vote__count">{count}</span>}
            </button>
          );
        })}
      </div>
      {closed && motm.votes === 0 && !motm.memberId && (
        <p className="field__note">Nobody voted, and the window has passed.</p>
      )}
      {!closed && !myMemberId && <p className="field__note">You need an account in this group to vote.</p>}
    </div>
  );
}

/**
 * Team average over the players the group actually rated.
 *
 * Counting unrated players as 6.5 would drag every average towards the middle
 * and make a team of two rated stars look worse than a team of eleven nobodies.
 * If nobody has rated anyone yet there is no average to show, so it says so.
 */
function teamAverage(players) {
  const scored = players.map(matchRating).filter((r) => r != null);
  if (!scored.length) return 'not rated yet';
  const avg = (scored.reduce((s, r) => s + r, 0) / scored.length).toFixed(1);
  return scored.length === players.length ? `${avg} avg` : `${avg} avg · ${scored.length}/${players.length}`;
}

/** The recorded game, dressed for the Pitch component. */
function toTokens(players, motmMemberId) {
  return players.map((p) => {
    const rating = matchRating(p);
    const badges = [
      p.goals > 0 ? `⚽${p.goals}` : '',
      p.assists > 0 ? `🅰️${p.assists}` : '',
    ].filter(Boolean).join(' ');
    return {
      ...p,
      assignedPosition: p.position,
      _rating: rating,
      _tone: ratingTone(rating),
      _star: p.memberId === motmMemberId,
      _badges: badges || p.position,
    };
  });
}

/** One player's line: their name, the group's running average, and 1-10. */
function RateLine({ player, peer, mine, draft, onPick, disabled }) {
  const score = mine ?? draft;
  return (
    <div className={`rate__row ${mine ? 'rate__row--locked' : ''}`}>
      <span className="rate__name">
        {player.name}
        {peer?.count > 0 && (
          <span className="rate__avg">
            group {peer.average.toFixed(1)} · {peer.count} rating{peer.count === 1 ? '' : 's'}
          </span>
        )}
      </span>

      {mine ? (
        <span className="rate__verdict">
          <span className={`rate__score rate__score--${ratingTone(mine)}`}>{mine}</span>
          <span className="rate__word">{ratingWord(mine)}</span>
        </span>
      ) : (
        <>
          <div className="scale" role="group" aria-label={`Rate ${player.name} out of 10`}>
            {RATING_STEPS.map((n) => (
              <button
                type="button"
                key={n}
                className={`scale__step ${draft === n ? 'scale__step--on' : ''}`}
                onClick={() => onPick(draft === n ? null : n)}
                disabled={disabled}
                aria-pressed={draft === n}
              >
                {n}
              </button>
            ))}
          </div>
          <span className="rate__word rate__word--draft">
            {score ? ratingWord(score) : 'not rated'}
          </span>
        </>
      )}
    </div>
  );
}

function RateRow({ game, myMemberId, myRatings, draft, setDraft, onSubmit, busy, window: win }) {
  const others = game.participants.filter((p) => p.memberId !== myMemberId);
  const mineCount = others.filter((p) => myRatings[p.memberId]).length;
  const submitted = mineCount > 0;
  const closed = !win.open;
  const picked = others.filter((p) => draft[p.memberId]).length;

  if (!myMemberId) {
    return (
      <div className="vote">
        <h3 className="vote__title">How did everyone play?</h3>
        <p className="field__note">You need an account in this group to rate players.</p>
      </div>
    );
  }

  // Closed and you never submitted: there is nothing to show and nothing to do,
  // so don't leave a dead prompt sitting on an old game forever.
  if (closed && !submitted) {
    return (
      <div className="vote">
        <h3 className="vote__title">
          How did everyone play?
          <span className="vote__tag vote__tag--shut">
            <Icon name="lock" size={11} />
            closed {closedOnText(win.closesAt)}
          </span>
        </h3>
        <p className="field__note">
          Ratings close {VOTING_WINDOW_HOURS} hours after a game is logged. You didn&apos;t rate anyone
          on this one.
        </p>
      </div>
    );
  }

  const visible = submitted || closed ? others.filter((p) => myRatings[p.memberId]) : others;

  return (
    <div className="vote">
      <h3 className="vote__title">
        How did everyone play?
        {submitted || closed ? (
          <span className="vote__tag vote__tag--shut">
            <Icon name="lock" size={11} />
            {submitted ? 'submitted' : 'closed'}
          </span>
        ) : (
          <span className="vote__tag">{win.label}</span>
        )}
      </h3>

      <p className="field__note" style={{ marginTop: 0, marginBottom: 12 }}>
        {submitted ? (
          <>
            You rated {mineCount} of {others.length}. Everyone&apos;s score is the average of what the
            group said, so it keeps sharpening as the rest come in.
          </>
        ) : (
          <>
            Out of ten, the way the papers do it — 6 is a solid game, 8 is the reason you remember it.
            Change your mind as often as you like; <strong>nothing is saved until you submit</strong>,
            and after that it can&apos;t be edited.
          </>
        )}
      </p>

      <div className="rate">
        {visible.map((p) => (
          <RateLine
            key={p.id}
            player={p}
            peer={game.peer?.[p.memberId]}
            mine={myRatings[p.memberId]}
            draft={draft[p.memberId] ?? null}
            disabled={busy}
            onPick={(n) =>
              setDraft((prev) => {
                const next = { ...prev };
                if (n == null) delete next[p.memberId];
                else next[p.memberId] = n;
                return next;
              })
            }
          />
        ))}
      </div>

      {!submitted && !closed && (
        <div className="rate__submit">
          <span className="rate__progress">
            {picked} of {others.length} rated
          </span>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => onSubmit(game.id, draft, others.length)}
            disabled={busy || picked === 0}
          >
            {busy ? 'Saving…' : picked === 0 ? 'Pick some ratings' : `Submit ${picked}`}
          </button>
        </div>
      )}
    </div>
  );
}

function GameCard({ game, groupId, isAdmin, myMemberId, onVote, voting, votingFor, onDelete, myRatings, onSubmitRatings, defaultOpen, now }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  // Held here rather than inside RateRow, which unmounts when the card is
  // collapsed — half a set of ratings should survive a stray tap on the header.
  const [draft, setDraft] = useState({});
  const win = votingState(game, now);
  const navigate = useNavigate();
  const date = new Date(game.created_at);
  const dateStr = date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });

  const motm = game.motm;
  const winnerName = motm.memberId ? game.participants.find((p) => p.memberId === motm.memberId)?.name : null;
  const myVote = game.votes.find((v) => v.voter_member_id === myMemberId)?.recipient_member_id ?? null;

  const teams = [
    { label: 'TEAM A', variant: 'a', players: game.participants.filter((p) => p.team === 'A') },
    { label: 'TEAM B', variant: 'b', players: game.participants.filter((p) => p.team === 'B') },
  ];

  // Shown on the closed card, because the whole point of this feature is that
  // people rate later — and they won't open twelve games looking for the one
  // they still owe.
  // Only nags people who were actually there: someone who missed the game can
  // still rate, but shouldn't be told they owe twelve opinions.
  const iPlayed = Boolean(myMemberId) && game.participants.some((p) => p.memberId === myMemberId);
  // Ratings go in as one submission, so this is a yes/no, not a tally. Counting
  // down individual players would be counting down a draft nobody has sent.
  const owed = iPlayed && win.open && Object.keys(myRatings).length === 0;

  return (
    <div className="hist">
      <button type="button" className="hist__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div className="hist__headmain">
          <span className="hist__date">{dateStr}</span>
          <span className="hist__score">
            <em className="hist__score--a">{game.score_a ?? 0}</em>
            <span className="hist__dash">–</span>
            <em className="hist__score--b">{game.score_b ?? 0}</em>
          </span>
          {winnerName && (
            <span className="hist__motm">
              <Icon name="star" size={12} /> {winnerName}
              {motm.votes > 0 && ` · ${motm.votes} vote${motm.votes === 1 ? '' : 's'}`}
            </span>
          )}
          {owed && <span className="hist__todo">Rate the players · {win.label}</span>}
        </div>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} />
      </button>

      {open && (
        <div className="hist__body">
          <div className="pitches">
            {teams.map((t) => (
              <section key={t.label} className="pitch-block">
                <div className="pitch-block__head">
                  <h3 className={`pitch-block__label pitch-block__label--${t.variant}`}>{t.label}</h3>
                  <span className="pitch-block__avg">{teamAverage(t.players)}</span>
                </div>
                <Pitch players={toTokens(t.players, motm.memberId)} variant={t.variant} />
              </section>
            ))}
          </div>

          <p className="field__note">
            Ratings out of 10 come from what the group said below, plus goals and assists — an average
            game is 6.5. A dash means nobody has rated that player. Tap a name below for their profile.
          </p>

          <div className="hist__teams">
            {teams.map((t) => (
              <div key={t.label} className="hist__team">
                <h3 className={`hist__teamlabel hist__teamlabel--${t.variant}`}>{t.label}</h3>
                <ul className="hist__list">
                  {t.players.map((p) => (
                    <li key={p.id}>
                      <span className="hist__pos">{p.position}</span>
                      <Link to={`/g/${groupId}/player/${p.memberId}`} className="hist__name">
                        {p.name}
                      </Link>
                      <span className="hist__stats">
                        {p.goals > 0 && <span>⚽{p.goals}</span>}
                        {p.assists > 0 && <span>🅰️{p.assists}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <RateRow
            game={game}
            myMemberId={myMemberId}
            myRatings={myRatings}
            draft={draft}
            setDraft={setDraft}
            onSubmit={onSubmitRatings}
            busy={voting && votingFor === game.id}
            window={win}
          />

          <VoteRow
            game={game}
            motm={motm}
            myMemberId={myMemberId}
            myVote={myVote}
            voting={voting && votingFor === game.id}
            onVote={(recipientId) => onVote(game.id, recipientId)}
            window={win}
          />

          {isAdmin && (
            <div className="hist__actions">
              <button
                type="button"
                className="btn btn--outline btn--sm"
                onClick={() => navigate(`/g/${groupId}/log?game=${game.id}`)}
              >
                <Icon name="pencil" size={14} />
                Fix this result
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => onDelete(game)}>
                <Icon name="trash" size={14} />
                Delete
              </button>
              {game.edits > 0 && (
                <span className="hist__edited">
                  edited {game.edits} time{game.edits === 1 ? '' : 's'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Leaderboard({ games, groupId }) {
  const rows = useMemo(() => {
    const by = {};
    games.forEach((g) => {
      const diff = (g.score_a ?? 0) - (g.score_b ?? 0);
      const teamAWon = diff > 0;
      g.participants.forEach((p) => {
        const r = (by[p.memberId] ||= {
          memberId: p.memberId,
          name: p.name,
          played: 0,
          goals: 0,
          assists: 0,
          wins: 0,
          draws: 0,
          motm: 0,
        });
        r.played += 1;
        r.goals += p.goals || 0;
        r.assists += p.assists || 0;
        if (diff === 0) r.draws += 1;
        else if ((p.team === 'A') === teamAWon) r.wins += 1;
        if (g.motm.memberId === p.memberId) r.motm += 1;
      });
    });
    return Object.values(by)
      .map((r) => ({ ...r, winRate: r.played ? r.wins / r.played : 0 }))
      .sort((a, b) => b.winRate - a.winRate || b.played - a.played || b.goals + b.assists - (a.goals + a.assists));
  }, [games]);

  if (!rows.length) return null;

  return (
    <section className="board">
      <h2 className="team-heading">ALL TIME</h2>
      <div className="board__scroll">
        <table className="board__table">
          <thead>
            <tr>
              <th scope="col">Player</th>
              <th scope="col">P</th>
              <th scope="col">W</th>
              <th scope="col">D</th>
              <th scope="col" className="board__win">Win %</th>
              <th scope="col">⚽</th>
              <th scope="col">🅰️</th>
              <th scope="col">⭐</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.memberId}>
                <th scope="row">
                  <Link to={`/g/${groupId}/player/${r.memberId}`}>{r.name}</Link>
                </th>
                <td>{r.played}</td>
                <td>{r.wins}</td>
                <td>{r.draws}</td>
                <td className="board__win">
                  <span className="winbar" style={{ '--pct': `${Math.round(r.winRate * 100)}%` }}>
                    {Math.round(r.winRate * 100)}%
                  </span>
                </td>
                <td>{r.goals}</td>
                <td>{r.assists}</td>
                <td>{r.motm}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="field__note">
        P played · W won · D drawn · ⭐ man of the match. Win % counts wins only, so a run of draws reads
        as 0% — that's what the D column disambiguates. Tap a name for their profile.
      </p>
    </section>
  );
}

export default function HistoryScreen() {
  const { group, isAdmin, myMemberId } = useGroup();

  // One clock for the whole screen, so every card agrees about the time and a
  // window that runs out while you're looking at it closes itself. Half a minute
  // is fine: the finest label is minutes, and the database is the real gate.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);

  const [voting, setVoting] = useState(false);
  const [votingFor, setVotingFor] = useState(null);
  const [voteError, setVoteError] = useState('');

  const { data, loading, error, reload } = useAsync(async () => {
    const rows = await q(
      supabase
        .from('games')
        .select('id, score_a, score_b, created_at, man_of_the_match_id')
        .eq('group_id', group.id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false }),
      'Loading past games'
    );

    if (!rows?.length) return { games: [], members: [] };

    const ids = rows.map((g) => g.id);

    const participants = await q(
      supabase
        .from('game_participants')
        .select(
          'id, game_id, member_id, team, position, goals, assists, performance, group_members(display_name, profile_id, profiles(display_name))'
        )
        .in('game_id', ids),
      'Loading who played'
    );

    // Votes and the edit log are extras: if the tables or policies aren't there
    // yet, the page still renders the games rather than blanking.
    const { data: voteRows } = await supabase
      .from('honours')
      .select('game_id, voter_member_id, recipient_member_id')
      .in('game_id', ids)
      .eq('honour_type', 'motm');
    const { data: editRows } = await supabase.from('game_edits').select('game_id').in('game_id', ids);
    const { data: ratingRows } = await supabase
      .from('player_ratings')
      .select('game_id, rater_member_id, subject_member_id, score')
      .in('game_id', ids);

    const peerByGame = aggregateRatings(ratingRows || []);
    const myRatings = {};
    (ratingRows || []).forEach((r) => {
      if (r.rater_member_id !== myMemberId) return;
      (myRatings[r.game_id] ||= {})[r.subject_member_id] = r.score;
    });

    const members = await q(
      supabase.from('group_members').select('id, attributes').eq('group_id', group.id),
      'Loading the squad'
    );

    const nameFor = (gm) =>
      (gm?.profile_id ? gm?.profiles?.display_name : null) || gm?.display_name || 'Unnamed';

    const byGame = {};
    (participants || []).forEach((p) => {
      (byGame[p.game_id] ||= []).push({
        id: p.id,
        memberId: p.member_id,
        name: nameFor(p.group_members),
        team: p.team,
        position: p.position,
        goals: p.goals || 0,
        assists: p.assists || 0,
        // Raw, so applyPeerRatings can tell "nobody said anything" from
        // "somebody said 3". The maths defaults to 3 downstream anyway.
        performance: p.performance ?? null,
      });
    });

    const editCounts = {};
    (editRows || []).forEach((e) => (editCounts[e.game_id] = (editCounts[e.game_id] || 0) + 1));

    const games = rows.map((g) => {
      const votes = (voteRows || []).filter((v) => v.game_id === g.id);
      return {
        ...g,
        participants: byGame[g.id] || [],
        votes,
        edits: editCounts[g.id] || 0,
        peer: peerByGame[g.id] || {},
        motm: resolveMotm({ votes, adminPickId: g.man_of_the_match_id }),
      };
    });

    // Ratings cast after the game override what was logged at the time, so the
    // pitch view shows what the group actually thought rather than one person's
    // guess on the night.
    return { games: applyPeerRatings(games, peerByGame), members: members || [], myRatings };
  }, [group.id, myMemberId]);

  const games = data?.games || [];

  const accuracy = useMemo(
    () => (data ? balanceAccuracy(data.members, data.games) : null),
    [data]
  );

  const deleteGame = async (game) => {
    const when = new Date(game.created_at).toLocaleDateString();
    const ok = window.confirm(
      `Delete the ${game.score_a}–${game.score_b} game from ${when}?\n\n` +
        `Its goals, assists and votes go with it, and every player's rating is recalculated ` +
        `from the remaining games. This cannot be undone.`
    );
    if (!ok) return;
    setVoting(true);
    setVoteError('');
    try {
      await q(supabase.from('games').delete().eq('id', game.id), 'Deleting the game');
      // Ratings are derived by replaying history, so removing a game has to
      // trigger a replay — otherwise everyone keeps an offset earned in a match
      // that no longer exists.
      await recomputeGroupRatings(group.id);
      await reload();
    } catch (err) {
      setVoteError(explain(err));
    } finally {
      setVoting(false);
    }
  };

  /**
   * The whole set goes in at once.
   *
   * Rating used to save on every tap, which meant your first opinion was
   * committed before you had looked at the second player — and with ratings
   * final, a mis-tap was permanent from the instant your thumb landed. Holding
   * a draft and sending it in one insert makes the deliberate act deliberate:
   * fiddle as much as you like, then one button, once.
   */
  const submitRatings = async (gameId, draft, totalPlayers) => {
    if (!myMemberId) return;
    const rows = Object.entries(draft)
      .filter(([, score]) => Number.isFinite(score))
      .map(([subjectMemberId, score]) => ({
        game_id: gameId,
        rater_member_id: myMemberId,
        subject_member_id: subjectMemberId,
        score,
      }));
    if (!rows.length) return;

    // One dialog at the end, not one per player — the point of the draft is that
    // the individual taps are cheap and only this is expensive.
    const skipped = totalPlayers - rows.length;
    const warning =
      `Submit ${rows.length} rating${rows.length === 1 ? '' : 's'}?\n\n` +
      (skipped > 0
        ? `You've left ${skipped} player${skipped === 1 ? '' : 's'} unrated, and you can't come back to this game.\n\n`
        : '') +
      `Ratings can't be changed once submitted.`;
    if (!window.confirm(warning)) return;

    setVoting(true);
    setVotingFor(gameId);
    setVoteError('');
    try {
      await q(supabase.from('player_ratings').insert(rows), 'Saving your ratings');
      // Form follows from these, so the whole history is replayed. Cheap for a
      // friend group, and it keeps ratings and form from drifting apart.
      await recomputeGroupRatings(group.id);
      await reload();
    } catch (err) {
      setVoteError(explain(err));
    } finally {
      setVoting(false);
      setVotingFor(null);
    }
  };

  const castVote = async (gameId, recipientId) => {
    if (!myMemberId) return;
    setVoting(true);
    setVotingFor(gameId);
    setVoteError('');
    try {
      await q(
        supabase.from('honours').delete().eq('game_id', gameId).eq('voter_member_id', myMemberId).eq('honour_type', 'motm'),
        'Clearing your previous vote'
      );
      if (recipientId) {
        await q(
          supabase.from('honours').insert({
            game_id: gameId,
            voter_member_id: myMemberId,
            recipient_member_id: recipientId,
            honour_type: 'motm',
          }),
          'Casting your vote'
        );
      }
      await reload();
    } catch (err) {
      setVoteError(explain(err));
    } finally {
      setVoting(false);
      setVotingFor(null);
    }
  };

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">HISTORY</h1>
          <p className="page__sub">
            {games.length} game{games.length === 1 ? '' : 's'} played
          </p>
        </div>
      </header>

      <ErrorBanner error={error || voteError} onRetry={error ? reload : undefined} />
      {loading && <Spinner label="Loading history…" />}

      {!loading && !error && games.length === 0 && (
        <EmptyState
          icon="trophy"
          text="No games logged yet. Balance a game, play it, then submit the result on the Log tab."
        />
      )}

      {accuracy && accuracy.decisive >= 3 && (
        <div className="accuracy">
          <span className="accuracy__value">{Math.round(accuracy.rate * 100)}%</span>
          <div>
            <p className="accuracy__title">of decided games went the way the ratings predicted</p>
            <p className="accuracy__note">
              Across {accuracy.decisive} game{accuracy.decisive === 1 ? '' : 's'} with a winner
              {accuracy.draws > 0 && `, ${accuracy.draws} draw${accuracy.draws === 1 ? '' : 's'} excluded`}. Near
              50% means the ratings carry no information yet — expected early on, worth investigating once you
              have a season behind you.
            </p>
          </div>
        </div>
      )}

      <div className="stack stack--tight">
        {games.map((g, i) => (
          <GameCard
            key={g.id}
            now={now}
            // Expand the newest game only while there's still something to do on
            // it. A settled game from three weeks ago opens to no actions.
            defaultOpen={i === 0 && votingState(g, now).open}
            game={g}
            groupId={group.id}
            isAdmin={isAdmin}
            myMemberId={myMemberId}
            onVote={castVote}
            voting={voting}
            votingFor={votingFor}
            onDelete={deleteGame}
            myRatings={data?.myRatings?.[g.id] || {}}
            onSubmitRatings={submitRatings}
          />
        ))}
      </div>

      {!loading && games.length > 0 && <Leaderboard games={games} groupId={group.id} />}
    </div>
  );
}
